// src/api/contracts.js
import Axios from "../config/axios-v1-config";

// Obtener todos los contratos con filtros opcionales
export const getAllContracts = async ({
  page,
  limit,
  query,
  orderField,
  sortType = 'desc',
  businessIdentifier,
  status,
  startDate,
  endDate,
}) => {
  try {
    const params = new URLSearchParams({
      page,
      limit,
      orderField,
      sortType,
      ...(query && { [query.type]: query.query }),
      ...(status && { status }),
      ...(businessIdentifier && { businessIdentifier }),
      ...(startDate && { startDateSigned: startDate, endDateSigned: endDate }),
    });

    const response = await Axios.get(`contracts?${params.toString()}`);
    return response.data;
  } catch (err) {
    console.error("Error in getAllContracts:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};

// Obtener un contrato específico por ID
export const getOneContract = async (id) => {
  try {
    const response = await Axios.get(`contracts/${id}`);
    return response.data;
  } catch (err) {
    console.error("Error in getOneContract:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};

// Actualizar un contrato específico por ID
export const updateContract = async (id, updatedData) => {
  try {
    const response = await Axios.patch(`contracts/${id}`, updatedData);
    return response.data;
  } catch (err) {
    console.error("Error in updateContract:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};

// Obtener el logo basado en el ID, tipo y tipo de ID
export const getLogo = async ({ id, idType }) => {
  try {
    const params = new URLSearchParams({ idType });
    const response = await Axios.get(`contracts/logo/${id}?${params.toString()}`);
    return response.data;
  } catch (err) {
    console.error("Error in getLogo:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};

// Metadata de los países del catálogo (sin `regions`): name, code, currency,
// timezone, divisionLabels y postalCodeRates. La consume CountryContext.
export const getCountries = async () => {
  try {
    const response = await Axios.get(`contracts/countries`);
    return response.data;
  } catch (err) {
    console.error("Error in getCountries:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};

// Obtener regiones de un país.
// `countryId` es obligatorio: el default a Chile que había acá enmascaraba a
// TODOS los llamadores que no lo pasaban. Los call sites lo toman de
// useCountry() y esperan a que `loading` sea false antes de llamar.
export const getRegions = async ({ countryId }) => {
  if (!countryId) {
    throw new Error("getRegions: falta countryId (debe venir de useCountry()).");
  }
  try {
    const params = new URLSearchParams({ countryId });
    const response = await Axios.get(`contracts/countries/regions?${params.toString()}`);
    return response.data;
  } catch (err) {
    console.error("Error in getRegions:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};

// Todas las comunas del país, en una lista plana ordenada y sin repetidos.
// Para los selectores que NO tienen paso previo de región (p. ej. la comuna
// fiscal del modal de usuario). Se apoya en que `contracts/countries/regions`
// devuelve las regiones completas, con sus `communes` anidadas: así es una sola
// request en vez de una por región.
export const getAllCommunes = async ({ countryId }) => {
  const regions = await getRegions({ countryId });
  if (!Array.isArray(regions)) return regions; // propaga { error } tal cual
  const names = new Set();
  regions.forEach((region) => {
    (region?.communes ?? []).forEach((commune) => {
      if (commune?.name) names.add(commune.name);
    });
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b, "es"));
};

// Obtener comunas de una región
export const getCommunes = async ({ countryId, regionId }) => {
  try {
    const params = new URLSearchParams({ countryId, regionId });
    const response = await Axios.get(`contracts/countries/communes?${params.toString()}`);
    return response.data;
  } catch (err) {
    console.error("Error in getCommunes:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};

// Crear un nuevo contrato
export const createContract = async (data) => {
  try {
    const response = await Axios.post(`contracts`, data);
    return response.data;
  } catch (err) {
    console.error("Error in createContract:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};

// Bloquear o desbloquear un contrato por no pago
export const togglePaymentBlock = async (contractId, { isBlocked, reason }) => {
  try {
    const response = await Axios.patch(`payment-block/${contractId}`, { isBlocked, reason });
    return response.data;
  } catch (err) {
    console.error("Error in togglePaymentBlock:", err.response?.data || 'Unknown error');
    return { error: err.response?.data || 'Unknown error' };
  }
};

// ── Renovación del contrato y contrato firmado ──
//
// El PDF firmado va al S3 de la plataforma por el proxy de archivos del BFF (el mismo
// camino que los comprobantes de transferencia, ver uploadPaymentProof en api/cobro.js):
// microservice-contracts no guarda archivos, solo la referencia y los metadatos.

// Carpeta del objeto en S3: `contratos-comercio/<operador>/<contrato>/<uuid v4>`, en minúscula.
// contracts exige el prefijo exacto `contratos-comercio/<operador>/<contrato>/` con los ids
// como los serializa Mongo (hex en minúscula). El bucket es de lectura pública por URL: el
// uuid aleatorio, uno por subida, hace que la URL del objeto no se pueda adivinar a partir de
// los ids y la hora de la subida.
export const contractDocumentDir = (operatorId, contractId, uploadId) =>
  `contratos-comercio/${operatorId}/${contractId}/${uploadId}`.toLowerCase();

// Nombre con el que viaja el archivo en el multipart. ms-s3-ots arma la key con un nombre
// propio y solo conserva la extensión del original, y contracts exige que la key termine en
// `.pdf`: un nombre fijo evita que un "CONTRATO.PDF" termine rechazado. El nombre real del
// archivo viaja aparte, en `document.fileName` de la renovación.
const CONTRACT_DOCUMENT_UPLOAD_NAME = "contrato-firmado.pdf";

// Sube el PDF firmado y devuelve { url, key } del objeto. Lanza si la subida falla o si
// el proxy responde sin archivo (ms-s3-ots informa sus errores con un 2xx sin `files`).
// Cada llamada sube a una carpeta nueva (uuid propio); un reintento con el mismo archivo no
// llama de nuevo: reusa la referencia ya subida (ver RenewContractModal).
export const uploadContractDocument = async (file, { operatorId, contractId }) => {
  // `crypto.randomUUID` solo existe en contextos seguros (https o localhost), igual que el
  // `crypto.subtle` del sha256, que se calcula antes y ya falla ahí con su propio mensaje.
  if (typeof window.crypto?.randomUUID !== "function") {
    throw new Error("El navegador no ofrece crypto.randomUUID para la carpeta del contrato firmado.");
  }
  const fd = new FormData();
  fd.append("files", new Blob([file], { type: "application/pdf" }), CONTRACT_DOCUMENT_UPLOAD_NAME);
  fd.append("dir", contractDocumentDir(operatorId, contractId, window.crypto.randomUUID()));
  const response = await Axios.post("/files/s/upload/img", fd, {
    headers: { "Content-Type": "multipart/form-data" },
    // Un PDF de 10 MB con un enlace lento no entra en los 60 s de la instancia.
    timeout: 180000,
  });
  const uploaded = response?.data?.files?.[0];
  if (typeof uploaded?.location !== "string" || typeof uploaded?.key !== "string") {
    throw new Error("La subida del contrato firmado no devolvió el archivo.");
  }
  return { url: uploaded.location, key: uploaded.key };
};

// Renueva el contrato con el PDF ya subido.
// Body: { dateSigned, startDate, endDate (null = indefinida), reactivate, document: { url,
// key, fileName, size, sha256 } }, fechas 'YYYY-MM-DD'. Responde { document, deduplicated }
// (sin el contrato: el detalle se refresca con getOneContract). En error devuelve
// { error, status }; `status` null = la respuesta nunca llegó (red, timeout) y la renovación
// pudo haberse guardado igual.
export const renewContract = async (id, body) => {
  try {
    const response = await Axios.post(`contracts/${id}/renewal`, body);
    return response.data;
  } catch (err) {
    console.error("Error in renewContract:", err.response?.status ?? "sin respuesta");
    return { error: err.response?.data || "Unknown error", status: err.response?.status ?? null };
  }
};

// PDF de un contrato firmado como blob (descarga autenticada: la URL del objeto nunca llega
// al navegador). Lanza; con `responseType: 'blob'` el cuerpo del error también llega como
// Blob, así que el llamador lo lee con apiErrorMessageAsync.
export const downloadSignedContractDocument = (id, docId) =>
  Axios.get(`contracts/${id}/signed-documents/${docId}/file`, { responseType: "blob" });

// Branding del sidebar del usuario autenticado: { coBranding, logo, operatorName }.
//
// El backend resuelve el operador desde el JWT — directo si el usuario es del
// operador, vía contrato si es de un comercio — así que NO recibe ningún id: un
// id por parámetro sería una fuga cross-tenant.
//
// A diferencia del resto del archivo, este NO atrapa el error: el llamador
// (hooks/useSidebarBranding) necesita distinguir "falló" de "sin co-branding"
// para no envenenar su cache con un fallo de red.
export const getSidebarBranding = async () => {
  const response = await Axios.get("contracts/branding/sidebar");
  return response.data;
};